/**
 * State Migration Engine - Public API and Boot Runner.
 *
 * BOOT SEQUENCE (called from coreRuntime before any persistence reads):
 *
 *   1. recoverCrashedMigrations()
 *      - Restore any backups from migrations that crashed mid-way
 *
 *   2. migrateSyncLedger()
 *      - Add formatVersion to SyncLedger entries (v0→v1 one-time migration)
 *
 *   3. migrateACUUsageLedger()
 *      - Add formatVersion to ACUUsageLedger entries (v0→v1 one-time migration)
 *
 * All further migrations (payload, envelope) are triggered lazily at load time
 * by persistLoad() and LocalAdapter.read() when they detect stale versions.
 */

// ── Side-effect imports: ensure migration steps are registered in registry ──
import "./envelopeMigrations";
import "./payloadMigrations";

import { recoverCrashedMigrations }                from "./migrationRunner";
import { migrateSyncLedger, migrateACUUsageLedger } from "./ledgerVersioning";
import { MigrationHistory }                         from "./migrationHistory";

// ── Public API re-exports ────────────────────────────────────────────────────
export { runMigrations }                           from "./migrationRunner";
export { recoverCrashedMigrations }                from "./migrationRunner";
export { migrateSyncLedger, migrateACUUsageLedger } from "./ledgerVersioning";
export {
  migrationRegistry,
  SCOPE_ENVELOPE,
  SCOPE_PAYLOAD,
  SCOPE_SYNC_LEDGER,
  SCOPE_ACU_LEDGER,
  SCOPE_SNAPSHOT,
}                                                  from "./migrationRegistry";
export { MigrationHistory }                        from "./migrationHistory";
export { CURRENT_ENVELOPE_VERSION }                from "./envelopeMigrations";
export { CURRENT_PAYLOAD_VERSION, payloadNeedsMigration } from "./payloadMigrations";
export type {
  MigrationDefinition,
  MigrationResult,
  MigrationHistoryRecord,
  MigrationStatus,
  MigrationPathError,
  MigrationValidationError,
}                                                  from "./types";

/**
 * Run all boot-time migrations.
 * Must be called synchronously before any persistence reads occur.
 * Safe to call multiple times (all operations are idempotent).
 */
export function initMigrationEngine(): void {
  // Step 1: recover any crashed migrations from the previous session
  recoverCrashedMigrations();

  // Step 2: add formatVersion to ledger entries that predate versioning
  migrateSyncLedger();
  migrateACUUsageLedger();

  // Step 3: prune old migration history records (>7 days)
  MigrationHistory.prune();
}
